var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import _get from 'lodash/get';
import Api from './utils/api';
import { CancellablePromise } from './CancellablePromise';
export const generateConcurrentId = (() => {
    let concurrentId = 0;
    return () => `sdk-id-${concurrentId++}`;
})();
const DEFAULT_ENDPOINT = '/api';
const DEFAULT_METHOD = 'POST';
function getRequestOptions(options, axiosConfig) {
    const requestOptions = {
        concurrentId: generateConcurrentId(),
        requestConfig: {},
    };
    if (options) {
        if (axiosConfig) {
            Object.assign(requestOptions, {
                requestConfig: axiosConfig,
            });
        }
        if (Object.prototype.hasOwnProperty.call(options, 'concurrentId')) {
            Object.assign(requestOptions, { concurrentId: options.concurrentId });
        }
        if (Object.prototype.hasOwnProperty.call(options, 'collectRequest')) {
            Object.assign(requestOptions, { collectRequest: options.collectRequest });
        }
        if (Object.prototype.hasOwnProperty.call(options, 'retries') && options.retries) {
            Object.assign(requestOptions, {
                requestConfig: Object.assign(Object.assign({}, requestOptions.requestConfig), { 'axios-retry': { retries: options.retries } }),
            });
        }
        if (Object.prototype.hasOwnProperty.call(options, 'signal') && options.signal) {
            Object.assign(requestOptions, {
                requestConfig: Object.assign(Object.assign({}, requestOptions.requestConfig), { signal: options.signal }),
            });
        }
        if (Object.prototype.hasOwnProperty.call(options, 'timeout')) {
            Object.assign(requestOptions, { timeout: options.timeout });
        }
        if (Object.prototype.hasOwnProperty.call(options, 'headers')) {
            Object.assign(requestOptions, { headers: options.headers });
        }
    }
    return requestOptions;
}
function createSdkAction(sdkConfig, api, endpoint, scope, service, action) {
    return function sdkAction(data, initialOptions) {
        const options = sdkConfig.prepareRequestOptions
            ? sdkConfig.prepareRequestOptions(scope, service, action, initialOptions)
            : initialOptions;
        const actionURL = `${endpoint}/${scope}/${service}/${action}`;
        const requestOptions = getRequestOptions(options);
        return new CancellablePromise(api.request({
            url: actionURL,
            method: DEFAULT_METHOD,
            data: data,
            options: requestOptions,
        }), () => api.cancelRequest(requestOptions.concurrentId));
    };
}
export { CancellablePromise } from './CancellablePromise';
let concurrentId = 0;
export default function sdkFactory(config) {
    const sdkConfig = config || {};
    const axiosConfig = sdkConfig.axiosConfig;
    const endpoint = sdkConfig.endpoint || DEFAULT_ENDPOINT;
    const api = new Api({ config: axiosConfig }, sdkConfig === null || sdkConfig === void 0 ? void 0 : sdkConfig.handleRequestError);
    if (sdkConfig.csrfToken) {
        api.setCSRFToken(sdkConfig.csrfToken);
    }
    const baseSdk = (requestConfig, initialOptions) => {
        const options = sdkConfig.prepareRequestOptions
            ? sdkConfig.prepareRequestOptions(null, null, null, initialOptions)
            : initialOptions;
        const { url = DEFAULT_ENDPOINT, method = DEFAULT_METHOD, data, params } = requestConfig, restAxiosConfig = __rest(requestConfig, ["url", "method", "data", "params"]);
        const requestOptions = getRequestOptions(options, restAxiosConfig);
        return new CancellablePromise(api.request({
            url,
            method,
            data,
            params,
            options: requestOptions,
        }), () => api.cancelRequest(requestOptions.concurrentId));
    };
    const baseSdkMethods = {
        cancelRequest(id) {
            if (id) {
                return api.cancelRequest(id);
            }
        },
        setLang(lang) {
            if (lang) {
                const languageHeader = {
                    name: 'accept-language',
                    value: lang,
                };
                api.setDefaultHeader(languageHeader);
            }
        },
        setDefaultHeader: (...args) => {
            api.setDefaultHeader(...args);
        },
        setCSRFToken(csrfToken) {
            api.setCSRFToken(csrfToken);
        },
        getConcurrentId() {
            return String(++concurrentId);
        },
        isCancel(value) {
            return _get(value, ['isCancelled']) === true;
        },
    };
    Object.assign(baseSdk, baseSdkMethods);
    const scopes = {};
    const sdkProxy = new Proxy(baseSdk, {
        get: (sdk, scopeOrRootServiceName, scopeReceiver) => {
            if (scopeOrRootServiceName in baseSdk || typeof scopeOrRootServiceName !== 'string') {
                return Reflect.get(sdk, scopeOrRootServiceName, scopeReceiver);
            }
            if (!(scopeOrRootServiceName in scopes)) {
                scopes[scopeOrRootServiceName] = new Proxy({}, {
                    get: (scope, serviceOrRootActionName, serviceReceiver) => {
                        if (typeof serviceOrRootActionName !== 'string') {
                            return Reflect.get(scope, serviceOrRootActionName, serviceReceiver);
                        }
                        if (!(serviceOrRootActionName in scope)) {
                            const actions = {};
                            scope[serviceOrRootActionName] = new Proxy(() => { }, {
                                get: (service, actionName, actionReciever) => {
                                    if (typeof actionName !== 'string') {
                                        return Reflect.get(service, actionName, actionReciever);
                                    }
                                    // shotcut call for root scope witch call/apply
                                    if (actionName === 'apply' || actionName === 'call') {
                                        return (...args) => {
                                            let realThis, realArgs;
                                            if (actionName === 'apply') {
                                                [realThis, realArgs] = args;
                                            }
                                            else {
                                                [realThis, ...realArgs] = args;
                                            }
                                            return Reflect.apply(sdkProxy.root[scopeOrRootServiceName][serviceOrRootActionName], realThis, realArgs);
                                        };
                                    }
                                    else if (actionName === 'bind') {
                                        return (...bindArgs) => (...args) => Reflect.apply(sdkProxy.root[scopeOrRootServiceName][serviceOrRootActionName], bindArgs[0], bindArgs.slice(1).concat(args));
                                    }
                                    if (!(actionName in actions)) {
                                        actions[actionName] = createSdkAction(sdkConfig, api, endpoint, scopeOrRootServiceName, serviceOrRootActionName, actionName);
                                    }
                                    return actions[actionName];
                                },
                                apply(_target, thisArg, args) {
                                    // shortcut call for root scope
                                    return Reflect.apply(sdkProxy.root[scopeOrRootServiceName][serviceOrRootActionName], thisArg, args);
                                },
                            });
                        }
                        return scope[serviceOrRootActionName];
                    },
                });
            }
            return scopes[scopeOrRootServiceName];
        },
    });
    return sdkProxy;
}
